import { Select } from "antd";
import dayjs from "dayjs";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useTranslation } from "react-i18next";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowTimezoneOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import { TextField } from "../../../../../../../components/TextField";
import { TimePickerInput } from "../../../../../../../components/TimePicker/TimePicker";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  SelectContainer,
  TextFieldCss,
  popupOptionsCss,
} from "../RuleComponent.styles";

interface IInactivityFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  timezoneOptions?: IWorkflowTimezoneOption[];
  hospitalTimezone?: string;
}

export function InactivityFields({
  formik,
  timezoneOptions,
  hospitalTimezone,
}: IInactivityFieldsProps) {
  const { t } = useTranslation();

  const shouldShowConditionError = (field: keyof IRuleCondition): string => {
    const touchedArray = formik.touched.conditions as
      | FormikTouched<IRuleCondition>[]
      | undefined;
    const errorArray = formik.errors.conditions as
      | FormikErrors<IRuleCondition>[]
      | undefined;

    if (touchedArray && errorArray) {
      const touched = touchedArray[0];
      const error = errorArray[0];

      return touched?.[field] && error?.[field] ? (error[field] as string) : "";
    }

    return "";
  };

  // Parse executionTime string (HH:mm) to dayjs
  const getTimeValue = () => {
    if (formik.values.conditions[0]?.executionTime) {
      return dayjs(formik.values.conditions[0].executionTime, "HH:mm");
    }
    return undefined;
  };

  return (
    <>
      <SelectContainer
        isError={shouldShowConditionError("inactivityDays") !== ""}
      >
        <p>{t("workflows_rule_daysOfInactivity")}</p>
        <TextField
          name="conditions[0].inactivityDays"
          placeholder="1"
          value={formik.values.conditions[0].inactivityDays}
          label=""
          onChange={formik.handleChange}
          backgroudColor="white"
          className={`${TextFieldCss()} nopan nodrag`}
          type="number"
        />
        <ErrorMessage>
          {shouldShowConditionError("inactivityDays")}
        </ErrorMessage>
      </SelectContainer>

      <SelectContainer
        isError={shouldShowConditionError("executionTime") !== ""}
      >
        <p>{t("workflows_rule_timeToRunCheck")}</p>
        <TimePickerInput
          value={getTimeValue()}
          format="HH:mm"
          placeholder={t("workflows_rule_selectTime")}
          hideTimezone
          onChange={(time, timeString) => {
            const value = Array.isArray(timeString)
              ? timeString[0]
              : timeString;
            // Extract just HH:mm from the formatted string that includes timezone
            const timeOnly = value.split(" ")[0];
            formik.setFieldValue("conditions[0].executionTime", timeOnly);
          }}
        />
        <ErrorMessage>{shouldShowConditionError("executionTime")}</ErrorMessage>
      </SelectContainer>

      <SelectContainer isError={shouldShowConditionError("timezone") !== ""}>
        <p>{t("workflows_rule_timezone")}</p>
        <Select
          placeholder={t("workflows_rule_selectTimezone")}
          value={formik.values.conditions[0]?.timezone}
          suffixIcon={<ArrowDown />}
          options={
            timezoneOptions?.map((tz) => ({
              label: tz.label,
              value: tz.value,
              key: tz.value,
            })) ?? []
          }
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noTimezones")}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].timezone", value);
          }}
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString() ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
        <ErrorMessage>{shouldShowConditionError("timezone")}</ErrorMessage>
        {formik.values.conditions[0]?.timezone &&
          hospitalTimezone &&
          formik.values.conditions[0].timezone !== hospitalTimezone && (
            <span
              style={{
                color: "#F1801F",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t("workflows_rule_hospitalTimezone", {
                timezone: hospitalTimezone,
              })}
            </span>
          )}
      </SelectContainer>
    </>
  );
}
